import { useEffect, useState, useCallback, useRef } from "react";
import { Map, MapMarker, CustomOverlayMap } from "react-kakao-maps-sdk";
import { useDriverLocationControl } from "@/hooks/location/useDriverLocation";
import {
  DriverLocationWithInfo,
  DispatchStatus,
  DISPATCH_STATUS_LABEL,
  DISPATCH_STATUS_COLOR,
} from "@/interface/location";
import { Button } from "@/components/ui/Button";

// 마커 이미지 SVG (배차 상태별 색상)
const createMarkerSvg = (color: string) => {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="36" height="48" viewBox="0 0 36 48">
      <path fill="${color}" stroke="#fff" stroke-width="2" d="M18 2C9.163 2 2 9.163 2 18c0 12 16 28 16 28s16-16 16-28c0-8.837-7.163-16-16-16z"/>
      <circle fill="#fff" cx="18" cy="18" r="8"/>
      <path fill="${color}" d="M14 16h8v1h-8zM14 19h8v1h-8zM18 13l3 2h-6z"/>
    </svg>
  `)}`;
};

export default function LocationPage() {
  // 카카오맵 로딩 상태
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);

  // 카카오맵 SDK 로드
  useEffect(() => {
    if (window.kakao && window.kakao.maps) {
      window.kakao.maps.load(() => {
        setMapLoaded(true);
      });
    } else {
      setMapError("카카오맵 SDK를 불러올 수 없습니다");
    }
  }, []);

  // 기사 위치 데이터 조회
  const { data: locations, isLoading, refetch, dataUpdatedAt } = useDriverLocationControl();

  // 선택된 기사 (팝업 표시용)
  const [selectedDriver, setSelectedDriver] = useState<DriverLocationWithInfo | null>(null);

  // 필터 상태
  const [statusFilter, setStatusFilter] = useState<DispatchStatus | "ALL">("ALL");

  // 기사 검색
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  // 지도 인스턴스 ref
  const mapRef = useRef<kakao.maps.Map | null>(null);

  // 검색창 외부 클릭 시 드롭다운 닫기
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setIsSearchOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // 지도 중심 좌표 (기본: 서울)
  const [center, setCenter] = useState({ lat: 37.5665, lng: 126.978 });

  // 지도 줌 레벨 (숫자가 낮을수록 확대)
  const [zoomLevel, setZoomLevel] = useState(7);

  // 마지막 업데이트 시간 포맷
  const formatLastUpdate = useCallback(() => {
    if (!dataUpdatedAt) return "";
    const date = new Date(dataUpdatedAt);
    return date.toLocaleTimeString("ko-KR", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  }, [dataUpdatedAt]);

  // 필터링된 위치 데이터
  const filteredLocations = locations?.filter(loc => {
    if (statusFilter === "ALL") return true;
    return loc.dispatchStatus === statusFilter;
  }) || [];

  // 검색 결과
  const searchResults = locations?.filter(loc => {
    if (!searchQuery.trim()) return false;
    const query = searchQuery.toLowerCase();
    return (
      loc.driverName?.toLowerCase().includes(query) ||
      loc.driverCode?.toLowerCase().includes(query) ||
      loc.vehicleNumber?.toLowerCase().includes(query)
    );
  }) || [];

  // 기사 선택 (검색 결과에서)
  const handleSelectDriver = (driver: DriverLocationWithInfo) => {
    setSelectedDriver(driver);

    const newCenter = {
      lat: Number(driver.latitude),
      lng: Number(driver.longitude),
    };

    // 지도 인스턴스가 있으면 직접 이동
    if (mapRef.current) {
      const moveLatLng = new window.kakao.maps.LatLng(newCenter.lat, newCenter.lng);
      mapRef.current.setCenter(moveLatLng);
      mapRef.current.setLevel(3);
    }

    setCenter(newCenter);
    setZoomLevel(3);
    setIsSearchOpen(false);
    setSearchQuery("");
  };

  // 기사 수 통계
  const stats = {
    total: locations?.length || 0,
    none: locations?.filter(l => l.dispatchStatus === "NONE").length || 0,
    dispatched: locations?.filter(l => l.dispatchStatus === "DISPATCHED").length || 0,
    inProgress: locations?.filter(l => l.dispatchStatus === "IN_PROGRESS").length || 0,
    completed: locations?.filter(l => l.dispatchStatus === "COMPLETED").length || 0,
  };

  // 위치 데이터가 있으면 첫 번째 기사 위치로 중심 이동
  useEffect(() => {
    if (locations && locations.length > 0) {
      setCenter({
        lat: Number(locations[0].latitude),
        lng: Number(locations[0].longitude),
      });
    }
  }, [locations?.length]);

  // 마커 클릭 핸들러
  const handleMarkerClick = (driver: DriverLocationWithInfo) => {
    setSelectedDriver(driver);
    setCenter({
      lat: Number(driver.latitude),
      lng: Number(driver.longitude),
    });
  };

  // 팝업 닫기
  const handleClosePopup = () => {
    setSelectedDriver(null);
  };

  // 로딩 중
  if (!mapLoaded || isLoading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-120px)]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#83C41C] mx-auto mb-4"></div>
          <p className="text-gray-600">지도 로딩 중...</p>
        </div>
      </div>
    );
  }

  // 에러
  if (mapError) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-120px)]">
        <div className="text-center text-red-500">
          <p className="text-lg font-bold mb-2">지도를 불러올 수 없습니다</p>
          <p className="text-sm">{mapError}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-120px)] flex flex-col">
      {/* 상단 컨트롤 바 */}
      <div className="bg-white border-b px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h1 className="text-lg font-bold text-gray-800">기사 위치 관제</h1>

          {/* 상태 필터 버튼 */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setStatusFilter("ALL")}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                statusFilter === "ALL"
                  ? "bg-gray-800 text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              전체 ({stats.total})
            </button>
            <button
              onClick={() => setStatusFilter("IN_PROGRESS")}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                statusFilter === "IN_PROGRESS"
                  ? "bg-green-500 text-white"
                  : "bg-green-50 text-green-600 hover:bg-green-100"
              }`}
            >
              운송중 ({stats.inProgress})
            </button>
            <button
              onClick={() => setStatusFilter("DISPATCHED")}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                statusFilter === "DISPATCHED"
                  ? "bg-blue-500 text-white"
                  : "bg-blue-50 text-blue-600 hover:bg-blue-100"
              }`}
            >
              배차완료 ({stats.dispatched})
            </button>
            <button
              onClick={() => setStatusFilter("NONE")}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                statusFilter === "NONE"
                  ? "bg-gray-500 text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              대기 ({stats.none})
            </button>
            <button
              onClick={() => setStatusFilter("COMPLETED")}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                statusFilter === "COMPLETED"
                  ? "bg-gray-600 text-white"
                  : "bg-gray-100 text-gray-500 hover:bg-gray-200"
              }`}
            >
              완료 ({stats.completed})
            </button>
          </div>
        </div>

        <div className="flex items-center gap-4">
          {/* 기사 검색 */}
          <div className="relative" ref={searchRef}>
            <input
              type="text"
              placeholder="기사명, 차량번호 검색"
              value={searchQuery}
              onChange={e => {
                setSearchQuery(e.target.value);
                setIsSearchOpen(e.target.value.length > 0);
              }}
              onFocus={() => searchQuery && setIsSearchOpen(true)}
              className="w-52 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#83C41C] focus:border-transparent"
            />
            {/* 검색 결과 드롭다운 */}
            {isSearchOpen && searchResults.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto z-50">
                {searchResults.map(driver => (
                  <button
                    key={driver.driverCode}
                    onClick={() => handleSelectDriver(driver)}
                    className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center gap-2 border-b last:border-b-0"
                  >
                    <span
                      className="w-2 h-2 rounded-full flex-shrink-0"
                      style={{ backgroundColor: DISPATCH_STATUS_COLOR[driver.dispatchStatus] }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-800 truncate">
                        {driver.driverName || driver.driverCode}
                      </div>
                      <div className="text-xs text-gray-500 truncate">
                        {driver.vehicleNumber || "차량 미등록"}
                      </div>
                    </div>
                    <span className="text-xs text-gray-400">
                      {DISPATCH_STATUS_LABEL[driver.dispatchStatus]}
                    </span>
                  </button>
                ))}
              </div>
            )}
            {isSearchOpen && searchQuery && searchResults.length === 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm text-gray-500 text-center z-50">
                검색 결과가 없습니다
              </div>
            )}
          </div>

          <span className="text-sm text-gray-500">
            마지막 업데이트: {formatLastUpdate()}
          </span>
          <Button variant="primary" onClick={() => refetch()}>
            새로고침
          </Button>
        </div>
      </div>

      {/* 지도 영역 */}
      <div className="flex-1 relative">
        <Map
          center={center}
          style={{ width: "100%", height: "100%" }}
          level={zoomLevel}
          onClick={handleClosePopup}
          onZoomChanged={map => setZoomLevel(map.getLevel())}
          onCreate={map => (mapRef.current = map)}
        >
          {/* 기사 마커들 */}
          {filteredLocations.map(driver => (
            <MapMarker
              key={driver.driverCode}
              position={{
                lat: Number(driver.latitude),
                lng: Number(driver.longitude),
              }}
              image={{
                src: createMarkerSvg(DISPATCH_STATUS_COLOR[driver.dispatchStatus]),
                size: { width: 36, height: 48 },
                options: { offset: { x: 18, y: 48 } },
              }}
              onClick={() => handleMarkerClick(driver)}
              title={driver.driverName || driver.driverCode}
            />
          ))}

          {/* 선택된 기사 팝업 */}
          {selectedDriver && (
            <CustomOverlayMap
              position={{
                lat: Number(selectedDriver.latitude),
                lng: Number(selectedDriver.longitude),
              }}
              yAnchor={1.4}
            >
              <div className="bg-white rounded-lg shadow-xl border border-gray-200 p-4 min-w-[280px]">
                {/* 헤더 */}
                <div className="flex items-center justify-between mb-3 pb-2 border-b">
                  <div className="flex items-center gap-2">
                    <span
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: DISPATCH_STATUS_COLOR[selectedDriver.dispatchStatus] }}
                    />
                    <span className="font-bold text-gray-800">
                      {selectedDriver.driverName || "이름 없음"}
                    </span>
                    <span
                      className="text-xs px-2 py-0.5 rounded-full"
                      style={{
                        backgroundColor: DISPATCH_STATUS_COLOR[selectedDriver.dispatchStatus] + "20",
                        color: DISPATCH_STATUS_COLOR[selectedDriver.dispatchStatus],
                      }}
                    >
                      {DISPATCH_STATUS_LABEL[selectedDriver.dispatchStatus]}
                    </span>
                  </div>
                  <button
                    onClick={handleClosePopup}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>

                {/* 기사 정보 */}
                <div className="space-y-2 text-sm">
                  <div className="flex">
                    <span className="text-gray-500 w-20">연락처</span>
                    <span className="text-gray-800">{selectedDriver.driverMobileTel || "-"}</span>
                  </div>
                  <div className="flex">
                    <span className="text-gray-500 w-20">차량번호</span>
                    <span className="text-gray-800">{selectedDriver.vehicleNumber || "-"}</span>
                  </div>
                  <div className="flex">
                    <span className="text-gray-500 w-20">오늘 배차</span>
                    <span className="text-gray-800">{selectedDriver.todayOrderCount}건</span>
                  </div>
                </div>

                {/* 현재 운송 정보 */}
                {selectedDriver.currentOrderSlipCode && (
                  <div className="mt-3 pt-3 border-t">
                    <p className="text-xs text-gray-500 mb-2">현재 운송 중</p>
                    <div className="bg-gray-50 rounded p-2 text-sm">
                      <div className="font-medium text-gray-800 mb-1">
                        {selectedDriver.currentItemName || "품목 정보 없음"}
                      </div>
                      <div className="text-gray-600 text-xs">
                        {selectedDriver.currentInLocation && (
                          <span>상차: {selectedDriver.currentInLocation}</span>
                        )}
                        {selectedDriver.currentOutLocation && (
                          <span className="ml-2">→ 하차: {selectedDriver.currentOutLocation}</span>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* 위치 업데이트 시간 */}
                <div className="mt-3 pt-2 border-t text-xs text-gray-400">
                  위치 업데이트: {new Date(selectedDriver.recordedAt).toLocaleString("ko-KR")}
                </div>
              </div>
            </CustomOverlayMap>
          )}
        </Map>

        {/* 위치 데이터 없음 안내 */}
        {filteredLocations.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/10">
            <div className="bg-white rounded-lg shadow-lg p-6 text-center">
              <p className="text-gray-600 mb-2">표시할 기사 위치가 없습니다</p>
              <p className="text-sm text-gray-400">기사 앱에서 위치 정보를 전송해야 합니다</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
